import { useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  BookMarked,
  CheckCircle2,
  ExternalLink,
  Film,
  Gamepad2,
  Loader2,
  MessageSquareText,
  Sparkles,
} from 'lucide-react'
import clsx from 'clsx'
import { getGameEntry, type GameEntry } from '@quebon/games'
import { isDevToolsVisible } from '@quebon/shared-learning'
import { contentApi } from '@/api/content'
import { gameApi, type LessonGamePath } from '@/api/games'
import { quizScriptApi } from '@/api/quizScript'
import type { ContentQuizLocation } from '@/api/quizScript'
import { CoachingVideoPlayer } from '@/components/admin/coaching/component-details/parts/CoachingVideoPlayer'
import { ToonPanelList } from '@/components/admin/coaching/component-details/parts/ToonPanelList'
import { QuizPreviewPanel } from '@/components/admin/diagnostics/QuizPreviewPanel'
import { useAuthStore } from '@/store/authStore'

type LessonQuizLocation = Extract<ContentQuizLocation, 'selected' | 'review' | 'checkup'>

const QUIZ_LOCATIONS: LessonQuizLocation[] = ['selected', 'review', 'checkup']

function parsePositiveId(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

export function parseLessonGamePath(
  searchParams: URLSearchParams,
  lessonId: number,
): LessonGamePath | null {
  const curriculumId = parsePositiveId(searchParams.get('curriculumId'))
  const courseId = parsePositiveId(searchParams.get('courseId'))
  const moduleId = parsePositiveId(searchParams.get('moduleId'))
  if (!curriculumId || !courseId || !moduleId || !Number.isSafeInteger(lessonId) || lessonId <= 0) {
    return null
  }
  return { curriculumId, courseId, moduleId, lessonId }
}

export function getLessonGamesQueryKey(path: LessonGamePath) {
  return [
    'lc-admin',
    'content-management',
    'lesson-games',
    path.curriculumId,
    path.courseId,
    path.moduleId,
    path.lessonId,
  ] as const
}

export interface SubjectInfo {
  raw: string
  school: string | null
  grade: number | null
  semester: number | null
  unit: number | null
  unitName: string | null
}

export function parseSubjectInfo(value: string | null | undefined): SubjectInfo | null {
  const raw = value?.trim()
  if (!raw) return null

  const match = raw.match(/^\[\s*([^\d\]]*?)\s*(\d+)\s*-\s*(\d+)\s*-\s*(\d+)\s*]\s*(.*)$/)
  if (!match) {
    return { raw, school: null, grade: null, semester: null, unit: null, unitName: null }
  }

  return {
    raw,
    school: match[1].trim() || null,
    grade: Number(match[2]),
    semester: Number(match[3]),
    unit: Number(match[4]),
    unitName: match[5].trim() || null,
  }
}

export default function ContentLessonDetailPage() {
  const { t } = useTranslation('admin')
  const userEmail = useAuthStore((state) => state.user?.email)
  const { lessonId: lessonIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const lessonId = Number(lessonIdParam)
  const validLessonId = Number.isSafeInteger(lessonId) && lessonId > 0
  const lessonGamePath = parseLessonGamePath(searchParams, lessonId)

  const lessonQuery = useQuery({
    queryKey: ['lc-admin', 'content-management', 'lesson', lessonId],
    queryFn: () => contentApi.getLessonDetail(lessonId),
    enabled: validLessonId,
    retry: false,
  })
  const qid = lessonQuery.data?.qid?.trim() || null

  const quetureInfoQuery = useQuery({
    queryKey: ['lc-admin', 'content-management', 'lesson-queture', lessonId, qid],
    queryFn: () => contentApi.getQuetureInfo(lessonId, qid ?? ''),
    enabled: validLessonId && qid != null,
    retry: false,
  })
  const quetureContentQuery = useQuery({
    queryKey: ['lc-admin', 'content-management', 'lesson-queture-content', qid],
    queryFn: () => contentApi.getQuetureContent(qid ?? ''),
    enabled: qid != null,
    retry: false,
  })
  const quizzesQuery = useQuery({
    queryKey: ['lc-admin', 'content-management', 'lesson-quizzes', lessonId],
    queryFn: async () => {
      const [selected, review, checkup] = await Promise.all([
        quizScriptApi.getContentQuizzes(lessonId, 'selected'),
        quizScriptApi.getContentQuizzes(lessonId, 'review'),
        quizScriptApi.getContentQuizzes(lessonId, 'checkup'),
      ])
      return { selected, review, checkup }
    },
    enabled: validLessonId,
    retry: false,
  })
  const gamesQuery = useQuery({
    queryKey: lessonGamePath
      ? getLessonGamesQueryKey(lessonGamePath)
      : ['lc-admin', 'content-management', 'lesson-games', 'missing-path'],
    queryFn: () => lessonGamePath
      ? gameApi.getAdminGamesByLessonPath(lessonGamePath)
      : Promise.reject(new Error('Lesson game path is missing')),
    enabled: lessonGamePath != null,
    retry: false,
  })

  if (!validLessonId || lessonQuery.isError) {
    return <LessonLoadError />
  }
  if (lessonQuery.isLoading || !lessonQuery.data) {
    return <LessonLoading />
  }

  const lesson = lessonQuery.data
  const detailTitle = lesson.bareTitle?.trim() || lesson.title
  const queturePoint = quetureInfoQuery.data?.queturePoint?.trim() || null
  const showDebugIds = import.meta.env.DEV && isDevToolsVisible(userEmail)

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 pb-12">
      <header className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-primary-50/60 px-5 py-5 sm:px-7">
          <Link
            to="/admin/contents"
            className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-primary-700"
          >
            <ArrowLeft className="size-4" aria-hidden />
            {t('contentManagement.lessonDetail.backToList')}
          </Link>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-700">
                {t('contentManagement.lessonDetail.eyebrow')}
              </p>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950"><ContentMetadataText text={detailTitle} /></h1>
              {queturePoint && (
                <div className="mt-3 flex items-start gap-2 text-sm">
                  <span className="shrink-0 font-semibold text-primary-700">
                    {t('contentManagement.lessonDetail.queturePoint')}
                  </span>
                  <span className="whitespace-pre-line text-slate-600"><ContentMetadataText text={queturePoint} /></span>
                </div>
              )}
            </div>
            {qid && showDebugIds && (
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500 shadow-sm">
                {t('contentManagement.lessonDetail.quetureId', { id: qid })}
              </span>
            )}
          </div>
        </div>
      </header>

      <QuetureContentSection
        info={quetureInfoQuery.data}
        content={quetureContentQuery.data}
        loading={quetureInfoQuery.isLoading || quetureContentQuery.isLoading}
      />
      <LessonGamesSection
        gameKeys={gamesQuery.data}
        loading={gamesQuery.isLoading}
        pathUnavailable={lessonGamePath == null || gamesQuery.isError}
      />
      <LessonQuizSection groups={quizzesQuery.data} loading={quizzesQuery.isLoading} />

      {!quetureInfoQuery.isLoading
        && !quetureContentQuery.isLoading
        && !quizzesQuery.isLoading
        && !gamesQuery.isLoading
        && lessonGamePath != null
        && !gamesQuery.isError
        && !hasQuetureContent(quetureInfoQuery.data, quetureContentQuery.data)
        && (gamesQuery.data?.length ?? 0) === 0
        && !hasAnyQuiz(quizzesQuery.data) && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <BookMarked className="mx-auto size-10 text-slate-300" aria-hidden />
            <p className="mt-3 text-sm font-medium text-slate-600">
              {t('contentManagement.lessonDetail.empty')}
            </p>
          </div>
        )}
    </div>
  )
}

type QuetureInfo = Awaited<ReturnType<typeof contentApi.getQuetureInfo>>
type QuetureContent = Awaited<ReturnType<typeof contentApi.getQuetureContent>>
type QuizGroups = Awaited<ReturnType<typeof quizScriptApi.getContentQuizzes>>

function hasQuetureContent(info: QuetureInfo | undefined, content: QuetureContent | undefined) {
  return Boolean(
    info?.queturePoint?.trim()
      || info?.hlsUrl
      || info?.summaryHlsUrl
      || content?.grade?.trim()
      || content?.qnIntro?.length
      || content?.qnOutro?.length,
  )
}

function hasAnyQuiz(groups: Record<LessonQuizLocation, QuizGroups> | undefined) {
  return QUIZ_LOCATIONS.some((location) => (groups?.[location].length ?? 0) > 0)
}

function QuetureContentSection({
  info,
  content,
  loading,
}: {
  info: QuetureInfo | undefined
  content: QuetureContent | undefined
  loading: boolean
}) {
  const { t } = useTranslation('admin')
  const subject = parseSubjectInfo(content?.grade)
  const videos = [
    info?.hlsUrl ? { key: 'main', title: t('contentManagement.lessonDetail.mainQueture'), url: info.hlsUrl } : null,
    info?.summaryHlsUrl ? { key: 'flash', title: t('contentManagement.lessonDetail.flashQueture'), url: info.summaryHlsUrl } : null,
  ].filter((video): video is { key: string; title: string; url: string } => video != null)
  const intro = content?.qnIntro?.filter((panel) => hasToonPanelContent(panel)) ?? []
  const outro = content?.qnOutro?.filter((panel) => hasToonPanelContent(panel)) ?? []
  const hasContent = videos.length > 0 || subject != null || intro.length > 0 || outro.length > 0

  if (!loading && !hasContent) return null

  return (
    <section className="flex flex-col gap-4" aria-labelledby="queture-content-title">
      <SectionHeading
        id="queture-content-title"
        icon={<Sparkles className="size-5" aria-hidden />}
        title={t('contentManagement.lessonDetail.quetureSection')}
        description={t('contentManagement.lessonDetail.quetureSectionDescription')}
        tone="violet"
      />
      {loading && !hasContent && <InlineLoading />}
      {subject && <SubjectInfoCard subject={subject} />}
      {videos.length > 0 && (
        <div className={clsx('grid gap-4', videos.length > 1 && 'lg:grid-cols-2')}>
          {videos.map((video) => (
            <article key={video.key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
                <Film className="size-4 text-violet-600" aria-hidden />
                <h3 className="text-sm font-bold text-slate-900">{video.title}</h3>
              </div>
              <div className="p-4">
                <CoachingVideoPlayer videoUrl={video.url} thumbUrl={info?.thumbnailUrl ?? undefined} />
              </div>
            </article>
          ))}
        </div>
      )}
      {(intro.length > 0 || outro.length > 0) && (
        <div className={clsx('grid gap-4', intro.length > 0 && outro.length > 0 && 'xl:grid-cols-2')}>
          {intro.length > 0 && (
            <ToonCard title={t('contentManagement.lessonDetail.toonIntro')}>
              <ToonPanelList stage="intro" panels={intro} />
            </ToonCard>
          )}
          {outro.length > 0 && (
            <ToonCard title={t('contentManagement.lessonDetail.toonOutro')}>
              <ToonPanelList stage="outro" panels={outro} />
            </ToonCard>
          )}
        </div>
      )}
    </section>
  )
}

function hasToonPanelContent(panel: {
  text?: string
  imageUrl?: string
  audioUrl?: string
  answer0?: string
  answer1?: string
}) {
  return Boolean(
    panel.text?.trim()
      || panel.imageUrl
      || panel.audioUrl
      || panel.answer0?.trim()
      || panel.answer1?.trim(),
  )
}

function SubjectInfoCard({ subject }: { subject: SubjectInfo }) {
  const { t } = useTranslation('admin')
  const parsed = subject.school != null && subject.grade != null && subject.semester != null && subject.unit != null

  return (
    <article className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
          <BookMarked className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-slate-900">{t('contentManagement.lessonDetail.subjectInfo')}</h3>
          {parsed ? (
            <>
              <div className="mt-2 flex flex-wrap gap-2">
                <SubjectChip>{subject.school}</SubjectChip>
                <SubjectChip>{t('contentManagement.lessonDetail.grade', { value: subject.grade })}</SubjectChip>
                <SubjectChip>{t('contentManagement.lessonDetail.semester', { value: subject.semester })}</SubjectChip>
                <SubjectChip>{t('contentManagement.lessonDetail.unit', { value: subject.unit })}</SubjectChip>
              </div>
              {subject.unitName && <p className="mt-3 text-base font-semibold text-emerald-950">{subject.unitName}</p>}
            </>
          ) : (
            <p className="mt-2 text-sm font-medium text-emerald-950">{subject.raw}</p>
          )}
        </div>
      </div>
    </article>
  )
}

function SubjectChip({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800">{children}</span>
}

function ToonCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
        <MessageSquareText className="size-4 text-violet-600" aria-hidden />
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </article>
  )
}

function LessonGamesSection({
  gameKeys,
  loading,
  pathUnavailable,
}: {
  gameKeys: string[] | undefined
  loading: boolean
  pathUnavailable: boolean
}) {
  const { t } = useTranslation('admin')
  const games = Array.from(
    new Set(gameKeys?.map((gameKey) => gameKey.trim()).filter(Boolean) ?? []),
  ).map((gameKey) => ({ gameKey, entry: getGameEntry(gameKey) }))

  if (pathUnavailable) {
    return (
      <section
        className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-5"
        aria-labelledby="game-path-unavailable-title"
      >
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <Gamepad2 className="size-5" aria-hidden />
          </span>
          <div>
            <h2 id="game-path-unavailable-title" className="text-sm font-bold text-amber-950">
              {t('contentManagement.lessonDetail.gamePathUnavailable')}
            </h2>
            <p className="mt-1 text-sm leading-6 text-amber-800">
              {t('contentManagement.lessonDetail.gamePathUnavailableDescription')}
            </p>
            <Link
              to="/admin/contents"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 underline underline-offset-2"
            >
              <ArrowLeft className="size-4" aria-hidden />
              {t('contentManagement.lessonDetail.reenterFromCurriculum')}
            </Link>
          </div>
        </div>
      </section>
    )
  }

  if (loading) {
    return (
      <section className="flex flex-col gap-4" aria-labelledby="game-content-title">
        <SectionHeading
          id="game-content-title"
          icon={<Gamepad2 className="size-5" aria-hidden />}
          title={t('contentManagement.lessonDetail.gameSection')}
          description={t('contentManagement.lessonDetail.gameSectionDescription')}
          tone="cyan"
        />
        <InlineLoading />
      </section>
    )
  }

  if (games.length === 0) return null

  return (
    <section className="flex flex-col gap-4" aria-labelledby="game-content-title">
      <SectionHeading
        id="game-content-title"
        icon={<Gamepad2 className="size-5" aria-hidden />}
        title={t('contentManagement.lessonDetail.gameSection')}
        description={t('contentManagement.lessonDetail.gameSectionDescription')}
        tone="cyan"
      />
      <div className="grid gap-4 lg:grid-cols-2">
        {games.map(({ gameKey, entry }) => (
          <LessonGameCard key={gameKey} gameKey={gameKey} entry={entry} />
        ))}
      </div>
    </section>
  )
}

function LessonGameCard({ gameKey, entry }: { gameKey: string; entry: GameEntry | undefined }) {
  const { t, i18n } = useTranslation('admin')

  if (!entry) {
    return (
      <article className="flex min-h-44 items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-white p-5 shadow-sm">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
          <Gamepad2 className="size-6" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-900">
            {t('contentManagement.lessonDetail.unknownGame')}
          </h3>
          <p className="mt-2 text-xs text-slate-500">
            {t('contentManagement.lessonDetail.unknownGameDescription')}
          </p>
        </div>
      </article>
    )
  }

  const playUrl = `/admin/game-math/${encodeURIComponent(gameKey)}`
  const localizedInfo = i18n.resolvedLanguage?.startsWith('en') ? entry.en : entry.ko

  return (
    <a
      href={playUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('contentManagement.lessonDetail.runGameLabel', { name: localizedInfo.name })}
      className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
    >
      <article className="grid h-full sm:grid-cols-[11rem_minmax(0,1fr)]">
        <div className="aspect-video overflow-hidden bg-slate-100 sm:aspect-auto">
          <img
            src={entry.image}
            alt=""
            className="size-full object-cover transition duration-300 group-hover:scale-[1.03]"
          />
        </div>
        <div className="flex min-w-0 flex-col p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-base font-bold text-slate-950">{localizedInfo.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-600">{localizedInfo.desc}</p>
            </div>
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-cyan-700 transition group-hover:bg-cyan-600 group-hover:text-white">
              <ExternalLink className="size-4" aria-hidden />
            </span>
          </div>
          <div className="mt-auto flex justify-end pt-4">
            <span className="shrink-0 text-xs font-bold text-cyan-700">
              {t('contentManagement.lessonDetail.runGame')}
            </span>
          </div>
        </div>
      </article>
    </a>
  )
}

function LessonQuizSection({
  groups,
  loading,
}: {
  groups: Record<LessonQuizLocation, QuizGroups> | undefined
  loading: boolean
}) {
  const { t } = useTranslation('admin')
  const [requestedLocation, setRequestedLocation] = useState<LessonQuizLocation>('selected')
  const [selectedQuizIndex, setSelectedQuizIndex] = useState(0)
  const availableLocations = useMemo(
    () => QUIZ_LOCATIONS.filter((location) => (groups?.[location].length ?? 0) > 0),
    [groups],
  )
  const activeLocation = availableLocations.includes(requestedLocation)
    ? requestedLocation
    : availableLocations[0]
  const quizzes = activeLocation ? groups?.[activeLocation] ?? [] : []
  const activeIndex = selectedQuizIndex < quizzes.length ? selectedQuizIndex : 0
  const activeQuiz = quizzes[activeIndex]

  if (!loading && availableLocations.length === 0) return null

  return (
    <section className="flex flex-col gap-4" aria-labelledby="quiz-content-title">
      <SectionHeading
        id="quiz-content-title"
        icon={<CheckCircle2 className="size-5" aria-hidden />}
        title={t('contentManagement.lessonDetail.quizSection')}
        description={t('contentManagement.lessonDetail.quizSectionDescription')}
        tone="amber"
      />
      {loading && <InlineLoading />}
      {!loading && activeLocation && activeQuiz && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-4 sm:px-5">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('contentManagement.lessonDetail.quizLocation')}>
              {availableLocations.map((location) => (
                <button
                  key={location}
                  type="button"
                  role="tab"
                  aria-selected={location === activeLocation}
                  onClick={() => {
                    setRequestedLocation(location)
                    setSelectedQuizIndex(0)
                  }}
                  className={clsx(
                    'rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
                    location === activeLocation
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'border border-slate-200 bg-white text-slate-600 hover:border-amber-300 hover:text-amber-800',
                  )}
                >
                  {t(`contentManagement.lessonDetail.quizLocations.${location}`)}
                  <span className="ml-1.5 opacity-75">{groups?.[location].length ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1" aria-label={t('contentManagement.lessonDetail.questionNavigation')}>
              {quizzes.map((quiz, index) => (
                <button
                  key={String(quiz.quizId)}
                  type="button"
                  onClick={() => setSelectedQuizIndex(index)}
                  aria-current={index === activeIndex ? 'true' : undefined}
                  className={clsx(
                    'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
                    index === activeIndex
                      ? 'bg-slate-900 text-white'
                      : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-400',
                  )}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <QuizPreviewPanel quizId={String(activeQuiz.quizId)} />
          </div>
        </div>
      )}
    </section>
  )
}

function SectionHeading({
  id,
  icon,
  title,
  description,
  tone,
}: {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  tone: 'violet' | 'amber' | 'cyan'
}) {
  const toneClassName = {
    violet: 'bg-violet-100 text-violet-700',
    amber: 'bg-amber-100 text-amber-700',
    cyan: 'bg-cyan-100 text-cyan-700',
  }[tone]

  return (
    <div className="flex items-start gap-3">
      <span className={clsx(
        'flex size-10 shrink-0 items-center justify-center rounded-xl',
        toneClassName,
      )}>
        {icon}
      </span>
      <div>
        <h2 id={id} className="text-lg font-bold text-slate-950">{title}</h2>
        <p className="mt-0.5 text-sm text-slate-500">{description}</p>
      </div>
    </div>
  )
}

function InlineLoading() {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-12 text-sm text-slate-500" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {t('contentManagement.lessonDetail.contentLoading')}
    </div>
  )
}

function LessonLoading() {
  const { t } = useTranslation('admin')
  return (
    <div className="flex min-h-80 items-center justify-center gap-2 text-sm text-slate-500" role="status">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      {t('contentManagement.lessonDetail.loading')}
    </div>
  )
}

function LessonLoadError() {
  const { t } = useTranslation('admin')
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-12 text-center">
      <p className="text-sm font-semibold text-rose-800">{t('contentManagement.lessonDetail.loadFailed')}</p>
      <Link to="/admin/contents" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-rose-700 underline">
        <ArrowLeft className="size-4" aria-hidden />
        {t('contentManagement.lessonDetail.backToList')}
      </Link>
    </div>
  )
}
